import type { RouteRecordRaw } from 'vue-router'
import useCorporateStore from '@shared/stores/useCorporateStore.js'
import useUserStore from '@shared/stores/useUserStore.js'
import useFeatureFlagStore, {
  type FeatureFlags,
} from '@shared/stores/useFeatureFlagStore'
import useEuStore from '@eu/app/stores/useEuStore.js'
import useDoctorOutreachStore from '@eu/app/stores/useDoctorOutreachStore.js'
import useAssessmentStore from '@shared/modules/assessment/stores/useAssessmentStore.js'
import { isFromApp } from '@shared/helpers/isFromApp'
import env from '@shared/env.js'
import type Corporate from '@shared/data/models/corporate.js'
import type User from '@shared/data/models/user.js'
import { canReturnToApp } from '@eu/app/helpers/canReturnToApp'
// type-only: names.js carries the literal route-name values (JSDoc const
// assertion) that RouteName below is derived from
import ROUTE from '@eu/app/router/appModule/names.js'
import type ASSESSMENT_ROUTE from '@shared/modules/assessment/router/names.js'

/** Every EU app route name — transition targets, guard fallbacks and
 *  useNavigation anchors are compile-checked against this union. */
export type RouteName = (typeof ROUTE)[keyof typeof ROUTE]

/** Entry route for a user whose corporate is already known. */
export const routeForCorporate = (
  corporate: Corporate,
  webOptimizedFunnel: boolean,
): RouteName => {
  switch (true) {
    case corporate.isDemo || corporate.isPrivateWithContract:
      return ROUTE.INSURANCE_VERIFICATION_PRIVATE
    case corporate.isPrivateInsurance:
      return ROUTE.INSURANCE_OVERVIEW
    default:
      return webOptimizedFunnel
        ? ROUTE.PRESCRIPTION_QUESTION
        : ROUTE.CODE_QUESTION
  }
}

/** First screen after the renewal assessment. */
export const routeAfterRenewalAssessment = (
  corporate: Corporate,
): RouteName => {
  if (corporate.insuranceType === 'public') {
    return ROUTE.DOCTOR_OUTREACH_PRIMER
  }
  return corporate.isDemo || corporate.isPrivateWithContract
    ? ROUTE.INSURANCE_VERIFICATION_PRIVATE
    : ROUTE.INSURANCE_OVERVIEW
}

/** A route a transition may land on: EU app routes plus assessment-module
 *  routes (the pre-signup self-test reentry, questionnaire detours). */
export type TransitionTarget =
  | RouteName
  | (typeof ASSESSMENT_ROUTE)[keyof typeof ASSESSMENT_ROUTE]

/** Shared input to transition predicates, guards and onboarding-step rules. */
export interface NavigationBaseCtx {
  flags: FeatureFlags
  corporate: Corporate | null
  user: User | null
  /** flags.webOptimizedFunnel && no subscription yet (excludes resubscribers) */
  isPreSubscriptionWebOptimizedFunnel: boolean
  /** flags.webOptimizedFunnel || existing subscription (resubscription flow) */
  isWebOptimizedFunnelOrResubscription: boolean
  /** The authenticated user entered a renewal flow. */
  isResubscription: boolean
  /** DiGA features are enabled on this build (MSK true, COPD false) */
  diga: boolean
  /** The session runs inside the native app webview */
  isFromApp: boolean
  /** In-app user whose flow ends by returning into the app (no subscription) */
  canReturnToApp: boolean
  /** The route the session entered the app on (deep-link entry detection) */
  startRoute: string
  /** The self-submit flow already showed the prescriber-information screen */
  prescriberInformationShown: boolean
  /** A practice from the renewal deep link is available for confirmation. */
  hasResolvedPractice: boolean
  /** Entry route of the next due questionnaire — null until
   *  getQuestionnaireConfig() has resolved, or when none is due */
  currentQuestionnaireRoute: TransitionTarget | null
}

export type TransitionEntry = {
  when?: (ctx: NavigationBaseCtx) => boolean
} & (
  | {
      target: TransitionTarget
      viaAssessment?: undefined
    }
  | {
      target: RouteName
      viaAssessment: {
        progressFrom?: RouteName
      }
    }
)

export interface ResolvedTransition {
  target: TransitionTarget
  assessment?: {
    finishRoute: RouteName
    progressFrom?: RouteName
  }
}

/** A single unnamed transition list, or lists keyed by user intent. */
export type NextDeclaration =
  | readonly TransitionEntry[]
  | Readonly<Record<string, readonly TransitionEntry[]>>

const isEntryList = (
  next: NextDeclaration,
): next is readonly TransitionEntry[] => Array.isArray(next)

/** First when-matching entry decides the transition; null = no transition. */
export const resolveTransitionDetails = (
  entries: readonly TransitionEntry[] | undefined,
  ctx: NavigationBaseCtx,
): ResolvedTransition | null => {
  const entry = entries?.find(({ when }) => when == null || when(ctx))
  if (entry == null) return null
  if (entry.viaAssessment && ctx.currentQuestionnaireRoute) {
    return {
      target: ctx.currentQuestionnaireRoute,
      assessment: {
        finishRoute: entry.target,
        progressFrom: entry.viaAssessment.progressFrom,
      },
    }
  }
  return { target: entry.target }
}

/** First when-matching entry decides the target; null = no transition. */
export const resolveTransition = (
  entries: readonly TransitionEntry[] | undefined,
  ctx: NavigationBaseCtx,
): string | null => resolveTransitionDetails(entries, ctx)?.target ?? null

/** Dev/test fail loud, prod log-and-stay: a navigation that cannot resolve is
 *  a programming error (wrong action name, missing declaration), never a
 *  legitimate user state. */
export const failNavigation = (message: string): null => {
  if (import.meta.env.PROD) {
    console.error(message)
    return null
  }
  throw new Error(message)
}

/**
 * Resolve a route's `next` declaration. A plain entry list takes no intent;
 * an intent map requires one of its declared intents. Everything else —
 * missing declaration, unknown intent, no entry matching the ctx — is
 * reported via failNavigation: the silent no-op convention is gone.
 */
export const resolveNextDetails = (
  routeName: string,
  next: NextDeclaration | undefined,
  intent: string | undefined,
  ctx: NavigationBaseCtx,
): ResolvedTransition | null => {
  if (next == null) {
    return failNavigation(
      `Route "${routeName}" declares no \`next\` transitions`,
    )
  }
  if (isEntryList(next)) {
    if (intent != null) {
      return failNavigation(
        `Route "${routeName}" declares a single unnamed transition — ` +
          `call navigateNext() without an intent (got "${intent}")`,
      )
    }
    const transition = resolveTransitionDetails(next, ctx)
    return (
      transition ??
      failNavigation(
        `No \`next\` entry on route "${routeName}" matched the navigation ctx`,
      )
    )
  }
  if (intent == null) {
    return failNavigation(
      `Route "${routeName}" requires an intent ` +
        `(declared: ${Object.keys(next).join(', ')})`,
    )
  }
  const entries = next[intent]
  if (entries == null) {
    return failNavigation(
      `Route "${routeName}" has no next intent "${intent}" ` +
        `(declared: ${Object.keys(next).join(', ')})`,
    )
  }
  const transition = resolveTransitionDetails(entries, ctx)
  return (
    transition ??
    failNavigation(
      `No entry of next intent "${intent}" on route "${routeName}" ` +
        'matched the navigation ctx',
    )
  )
}

export const resolveNext = (
  routeName: string,
  next: NextDeclaration | undefined,
  intent: string | undefined,
  ctx: NavigationBaseCtx,
): string | null =>
  resolveNextDetails(routeName, next, intent, ctx)?.target ?? null

/** The navigation contract an EU route may declare on its meta (see
 *  routes.ts). Kept as a standalone exact type because vue-router's RouteMeta
 *  extends Record<PropertyKey, unknown>, whose index signature disables
 *  excess-property checks — AppRouteRecord uses this exact shape instead, so
 *  meta typos are compile errors. */
export type AppRouteMeta = {
  /** Route is reachable without an authenticated session. The session
   *  bootstrap (hooks.js) redirects unauthenticated navigation to SIGN_UP
   *  on every route not marked public. Default: protected. */
  public?: boolean
  /** Analytics screen name tracked on afterEach — keep unique per route */
  name?: string
  /** Step number used to compute the progress bar, or a ctx-dependent fn */
  onboardingStep?: number | ((ctx: NavigationBaseCtx) => number | null)
  /** Forward transitions; map keys become navigateNext intent types. */
  next?: NextDeclaration
  /** Back transitions; omitted when the route has no predecessor. */
  back?: readonly TransitionEntry[]
}

declare module 'vue-router' {
  interface RouteMeta extends AppRouteMeta {}
}

type AppRouteRecordVariant<Route extends RouteRecordRaw> =
  Route extends RouteRecordRaw
    ? Omit<Route, 'name' | 'meta'> & {
        name: RouteName
        meta?: AppRouteMeta
      }
    : never

export type AppRouteRecord = AppRouteRecordVariant<RouteRecordRaw>

/** Snapshot the store state used by navigation rules. */
export const buildNavigationCtx = (): NavigationBaseCtx => {
  const flags = useFeatureFlagStore().flags
  const user = useUserStore().user
  const euStore = useEuStore()
  const doctorOutreachStore = useDoctorOutreachStore()

  return {
    flags,
    user,
    corporate: useCorporateStore().corporate,
    // The raw flag plus "no subscription yet" — excludes resubscribers.
    isPreSubscriptionWebOptimizedFunnel: Boolean(
      flags.webOptimizedFunnel && user?.currentSubscription == null,
    ),
    isWebOptimizedFunnelOrResubscription: Boolean(
      flags.webOptimizedFunnel || user?.currentSubscription != null,
    ),
    isResubscription: user?.currentSubscription != null,
    diga: env.features.diga,
    // Entry context: deep-link entry screens have no in-flow screen to go back to
    isFromApp: isFromApp(),
    canReturnToApp: canReturnToApp(),
    startRoute: euStore.startRoute,
    // Flow state consumed by transitions of converging screens
    prescriberInformationShown: euStore.prescriberInformationShown,
    hasResolvedPractice: doctorOutreachStore.resolvedPractice != null,
    currentQuestionnaireRoute:
      useAssessmentStore().currentQuestionnaireRoute ?? null,
  }
}
